import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users,
  Award,
  DollarSign,
  Search,
  Crown,
  Trash2,
  Shield,
  ArrowLeft,
  BookOpen,
  Activity,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Clock,
  UserPlus,
  CheckCircle,
  RefreshCw
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [recentActivity, setRecentActivity] = useState(null);
  const [courses, setCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 10;

  // Use proper API URL based on environment
  const getAPIURL = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8001/api';
    }
    return '/api';
  };
  const API_URL = window.location.origin ? `${window.location.origin}/api` : getAPIURL();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('spirify_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    }
  }, [currentPage, activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load stats
      const statsResponse = await fetch(`${API_URL}/admin/stats`, {
        headers: getAuthHeaders()
      });

      if (!statsResponse.ok) {
        if (statsResponse.status === 403) {
          setError('Accès refusé. Vous n\'êtes pas administrateur.');
          return;
        }
        throw new Error('Failed to load stats');
      }

      const statsData = await statsResponse.json();
      setStats(statsData);

      // Load initial users
      await loadUsers();

      // Load recent activity
      try {
        const activityResponse = await fetch(`${API_URL}/admin/recent-activity`, {
          headers: getAuthHeaders()
        });
        if (activityResponse.ok) {
          const activityData = await activityResponse.json();
          setRecentActivity(activityData);
        }
      } catch (e) {
        console.log('Activity endpoint not available');
      }

      // Load courses
      try {
        const coursesResponse = await fetch(`${API_URL}/courses`, {
          headers: getAuthHeaders()
        });
        if (coursesResponse.ok) {
          const coursesData = await coursesResponse.json();
          setCourses(coursesData || []);
        }
      } catch (e) {
        console.log('Courses endpoint not available');
      }

    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const usersResponse = await fetch(
        `${API_URL}/admin/users?skip=${currentPage * usersPerPage}&limit=${usersPerPage}`,
        { headers: getAuthHeaders() }
      );

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users || []);
        setTotalUsers(usersData.total || 0);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const togglePremium = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/admin/user/${userId}/toggle-premium`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        loadUsers();
        loadData();
      }
    } catch (err) {
      console.error('Error toggling premium:', err);
    }
  };

  const makeAdmin = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir promouvoir cet utilisateur en admin ?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/user/${userId}/make-admin`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        alert('Utilisateur promu en admin avec succès');
        loadUsers();
      }
    } catch (err) {
      console.error('Error making admin:', err);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/user/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        alert('Utilisateur supprimé avec succès');
        loadUsers();
        loadData();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Accès refusé</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredUsers = searchQuery 
    ? users.filter(user =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.username.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const tabs = [
    { id: 'overview', label: 'Aperçu', icon: BarChart3 },
    { id: 'users', label: 'Utilisateurs', icon: Users },
    { id: 'courses', label: 'Cours', icon: BookOpen },
    { id: 'activity', label: 'Activité', icon: Activity },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-purple-600" />
                  Administration
                </h1>
                <p className="text-sm text-gray-500">Gérez votre plateforme Spirify</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Utilisateurs</p>
                      <p className="text-3xl font-bold mt-1">{stats?.total_users || 0}</p>
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <UserPlus className="w-3 h-3" />
                        +{stats?.new_users_this_week || 0} cette semaine
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Premium</p>
                      <p className="text-3xl font-bold mt-1">{stats?.premium_users || 0}</p>
                      <p className="text-xs text-amber-600 mt-1">
                        {stats?.premium_conversion_rate || 0}% conversion
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <Crown className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">XP Total</p>
                      <p className="text-3xl font-bold mt-1">{(stats?.total_xp || 0).toLocaleString()}</p>
                      <p className="text-xs text-green-600 mt-1">
                        {stats?.active_users_this_week || 0} actifs cette semaine
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Award className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Revenu Estimé</p>
                      <p className="text-3xl font-bold mt-1">{stats?.estimated_monthly_revenue || 0}€</p>
                      <p className="text-xs text-gray-500 mt-1">par mois</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Contenu
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Leçons</span>
                      <span className="font-semibold">{stats?.total_lessons || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cours</span>
                      <span className="font-semibold">{courses.length || 4}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Répartition
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Gratuits</span>
                      <span className="font-semibold">{stats?.free_users || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Premium</span>
                      <span className="font-semibold text-amber-600">{stats?.premium_users || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Engagement
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Actifs/semaine</span>
                      <span className="font-semibold">{stats?.active_users_this_week || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">XP moyen</span>
                      <span className="font-semibold">
                        {stats?.total_users > 0 ? Math.round(stats.total_xp / stats.total_users) : 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity Preview */}
            {recentActivity?.recent_registrations?.length > 0 && (
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Dernières inscriptions
                  </h3>
                  <div className="space-y-3">
                    {recentActivity.recent_registrations.slice(0, 5).map((user, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <UserPlus className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{user.username}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{formatDate(user.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Utilisateurs ({totalUsers})</h2>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Utilisateur</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">XP</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Streak</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Statut</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Rôle</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{user.username}</td>
                        <td className="py-3 px-4 text-gray-600">{user.email}</td>
                        <td className="py-3 px-4">
                          <span className="font-semibold text-green-600">{user.xp || 0}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-orange-500">{user.streak || 0} 🔥</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.subscription_status === 'premium'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {user.subscription_status === 'premium' ? '⭐ Premium' : 'Free'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {user.role === 'admin' ? '🛡️ Admin' : 'User'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => togglePremium(user.id)}
                              title={user.subscription_status === 'premium' ? 'Révoquer Premium' : 'Activer Premium'}
                              className={user.subscription_status === 'premium' ? 'text-amber-600' : ''}
                            >
                              <Crown className="w-4 h-4" />
                            </Button>
                            {user.role !== 'admin' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => makeAdmin(user.id)}
                                title="Promouvoir en Admin"
                              >
                                <Shield className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => deleteUser(user.id)}
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Aucun utilisateur trouvé
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Affichage {currentPage * usersPerPage + 1} - {Math.min((currentPage + 1) * usersPerPage, totalUsers)} sur {totalUsers}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {currentPage + 1} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage >= totalPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Cours disponibles</h2>
              <p className="text-gray-500">{stats?.total_lessons || 0} leçons au total</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {courses.map((course) => (
                <Card key={course.id || course.slug} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="text-4xl">{course.icon || '📚'}</div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{course.name}</h3>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{course.description}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {course.lesson_count || 0} leçons
                          </span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            {course.total_xp || 0} XP
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {courses.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun cours trouvé</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-purple-600" />
                  Dernières inscriptions
                </h3>
                {recentActivity?.recent_registrations?.length > 0 ? (
                  <div className="space-y-3">
                    {recentActivity.recent_registrations.map((user, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="font-bold text-purple-600">
                              {user.username?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.username}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-400">{formatDate(user.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucune inscription récente</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Leçons complétées récemment
                </h3>
                {recentActivity?.recent_completions?.length > 0 ? (
                  <div className="space-y-3">
                    {recentActivity.recent_completions.map((completion, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">Leçon complétée</p>
                            <p className="text-sm text-gray-500">Score: {completion.score || 0}%</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-400">{formatDate(completion.completed_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucune activité récente disponible</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
